import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, authHeaders } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { formatCurrency } from '../lib/format';

// Shared helpers -----------------------------------------------------------

function useApiError() {
  const [error, setError] = useState('');
  const capture = useCallback((err) => {
    setError(err?.response?.data?.detail || err?.message || 'Request failed');
  }, []);
  return { error, setError, capture };
}

function useEmployees(token, enabled = true) {
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    if (!token || !enabled) return undefined;
    let ignore = false;
    api.get('/api/employees', authHeaders(token))
      .then((res) => { if (!ignore) setEmployees(res.data || []); })
      .catch(() => { if (!ignore) setEmployees([]); });
    return () => { ignore = true; };
  }, [token, enabled]);
  return employees;
}

function StatusBadge({ status }) {
  return <span className={`status-badge status-${String(status || '').toLowerCase()}`}>{status}</span>;
}

// Notifications ------------------------------------------------------------

export function NotificationsCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/notifications', authHeaders(token, { limit: 50 }))
      .then((res) => setItems(res.data || []))
      .catch(capture);
  }, [token, capture]);

  useEffect(() => { load(); }, [load]);

  const markRead = (id) => {
    api.post(`/api/notifications/${id}/read`, {}, authHeaders(token)).then(load).catch(capture);
  };
  const markAll = () => {
    api.post('/api/notifications/read-all', {}, authHeaders(token)).then(load).catch(capture);
  };

  const unread = items.filter((n) => !n.is_read).length;

  return (
    <section className="card">
      <div className="card-header-row">
        <h2>{t('notifications')} {unread ? <span className="pill">{unread}</span> : null}</h2>
        {unread ? <button type="button" className="ghost-button" onClick={markAll}>{t('markAllRead')}</button> : null}
      </div>
      {error ? <p className="error-text">{error}</p> : null}
      {items.length === 0 ? <p className="muted">{t('noNotifications')}</p> : (
        <ul className="notification-list">
          {items.map((n) => (
            <li key={n.id} className={n.is_read ? 'notification read' : 'notification unread'}>
              <div>
                <strong>{n.title}</strong>
                {n.body ? <p>{n.body}</p> : null}
                <small className="muted">{new Date(n.created_at).toLocaleString()}</small>
              </div>
              {!n.is_read ? (
                <button type="button" className="ghost-button" onClick={() => markRead(n.id)}>{t('markRead')}</button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Expense claims -----------------------------------------------------------

export function ExpensesCard({ canApprove = false }) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const activeLanguage = (useTranslation().i18n.resolvedLanguage || 'en').split('-')[0];
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: '', category: 'travel', expense_date: '', amount: '' });
  const [submitting, setSubmitting] = useState(false);
  const { error, capture, setError } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/expenses', authHeaders(token))
      .then((res) => setItems(res.data || []))
      .catch(capture);
  }, [token, capture]);

  useEffect(() => { load(); }, [load]);

  const submit = (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    api.post('/api/expenses', {
      title: form.title,
      category: form.category,
      expense_date: form.expense_date,
      amount: Number(form.amount),
    }, authHeaders(token))
      .then(() => { setForm({ title: '', category: 'travel', expense_date: '', amount: '' }); load(); })
      .catch(capture)
      .finally(() => setSubmitting(false));
  };

  const decide = (id, action) => {
    api.post(`/api/expenses/${id}/${action}`, {}, authHeaders(token)).then(load).catch(capture);
  };

  return (
    <section className="card">
      <h2>{t('expenseClaims')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      <form className="split split-three" onSubmit={submit}>
        <label>{t('title')}
          <input value={form.title} required onChange={(e) => setForm((c) => ({ ...c, title: e.target.value }))} />
        </label>
        <label>{t('category')}
          <select value={form.category} onChange={(e) => setForm((c) => ({ ...c, category: e.target.value }))}>
            <option value="travel">Travel</option>
            <option value="meals">Meals</option>
            <option value="supplies">Supplies</option>
            <option value="general">General</option>
          </select>
        </label>
        <label>{t('amount')} (ETB)
          <input type="number" min="0" step="0.01" value={form.amount} required onChange={(e) => setForm((c) => ({ ...c, amount: e.target.value }))} />
        </label>
        <label>{t('expenseDate')}
          <input type="date" value={form.expense_date} required onChange={(e) => setForm((c) => ({ ...c, expense_date: e.target.value }))} />
        </label>
        <div className="form-actions">
          <button type="submit" className="primary-button" disabled={submitting}>{submitting ? t('saving') : t('submitExpense')}</button>
        </div>
      </form>

      {items.length === 0 ? <p className="muted">{t('noExpenses')}</p> : (
        <table className="data-table">
          <thead>
            <tr><th>{t('title')}</th><th>{t('category')}</th><th>{t('amount')}</th><th>{t('expenseDate')}</th><th>Status</th>{canApprove ? <th>Actions</th> : null}</tr>
          </thead>
          <tbody>
            {items.map((x) => (
              <tr key={x.id}>
                <td>{x.title}</td>
                <td>{x.category}</td>
                <td>{formatCurrency(x.amount, activeLanguage)}</td>
                <td>{x.expense_date}</td>
                <td><StatusBadge status={x.status} /></td>
                {canApprove ? (
                  <td className="row-actions">
                    {x.status === 'submitted' ? (
                      <>
                        <button type="button" className="ghost-button" onClick={() => decide(x.id, 'approve')}>{t('approve')}</button>
                        <button type="button" className="ghost-button" onClick={() => decide(x.id, 'reject')}>{t('reject')}</button>
                      </>
                    ) : null}
                    {x.status === 'approved' ? (
                      <button type="button" className="ghost-button" onClick={() => decide(x.id, 'reimburse')}>{t('reimburse')}</button>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Assets -------------------------------------------------------------------

export function AssetsCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const employees = useEmployees(token);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ asset_tag: '', name: '', category: 'equipment', purchase_cost: '' });
  const [assignTarget, setAssignTarget] = useState({});
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/assets', authHeaders(token)).then((res) => setItems(res.data || [])).catch(capture);
  }, [token, capture]);
  useEffect(() => { load(); }, [load]);

  const create = (event) => {
    event.preventDefault();
    api.post('/api/assets', {
      asset_tag: form.asset_tag, name: form.name, category: form.category,
      purchase_cost: Number(form.purchase_cost || 0),
    }, authHeaders(token))
      .then(() => { setForm({ asset_tag: '', name: '', category: 'equipment', purchase_cost: '' }); load(); })
      .catch(capture);
  };
  const assign = (id) => {
    const employeeId = assignTarget[id];
    if (!employeeId) return;
    api.post(`/api/assets/${id}/assign`, { employee_id: Number(employeeId) }, authHeaders(token)).then(load).catch(capture);
  };
  const doReturn = (id) => api.post(`/api/assets/${id}/return`, {}, authHeaders(token)).then(load).catch(capture);
  const employeeName = (id) => employees.find((e) => e.id === id)?.full_name || '—';

  return (
    <section className="card">
      <h2>{t('assetRegister')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      <form className="split split-three" onSubmit={create}>
        <label>{t('assetTag')}<input value={form.asset_tag} required onChange={(e) => setForm((c) => ({ ...c, asset_tag: e.target.value }))} /></label>
        <label>{t('assetName')}<input value={form.name} required onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))} /></label>
        <label>{t('category')}
          <select value={form.category} onChange={(e) => setForm((c) => ({ ...c, category: e.target.value }))}>
            <option value="equipment">Equipment</option>
            <option value="vehicle">Vehicle</option>
            <option value="device">Device</option>
            <option value="furniture">Furniture</option>
          </select>
        </label>
        <label>{t('purchaseCost')} (ETB)<input type="number" min="0" step="0.01" value={form.purchase_cost} onChange={(e) => setForm((c) => ({ ...c, purchase_cost: e.target.value }))} /></label>
        <div className="form-actions"><button type="submit" className="primary-button">{t('addAsset')}</button></div>
      </form>

      {items.length === 0 ? <p className="muted">{t('noAssets')}</p> : (
        <table className="data-table">
          <thead><tr><th>{t('assetTag')}</th><th>{t('assetName')}</th><th>Status</th><th>{t('assignedTo')}</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id}>
                <td>{a.asset_tag}</td>
                <td>{a.name}</td>
                <td><StatusBadge status={a.status} /></td>
                <td>{a.assigned_employee_id ? employeeName(a.assigned_employee_id) : '—'}</td>
                <td className="row-actions">
                  {a.status === 'assigned' ? (
                    <button type="button" className="ghost-button" onClick={() => doReturn(a.id)}>{t('returnAsset')}</button>
                  ) : (
                    <>
                      <select value={assignTarget[a.id] || ''} onChange={(e) => setAssignTarget((c) => ({ ...c, [a.id]: e.target.value }))}>
                        <option value="">{t('selectEmployee')}</option>
                        {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
                      </select>
                      <button type="button" className="ghost-button" onClick={() => assign(a.id)}>{t('assign')}</button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Org chart ----------------------------------------------------------------

function OrgNode({ node, depth }) {
  return (
    <li className="org-node" style={{ marginInlineStart: depth * 16 }}>
      <div className="org-node-label">
        <strong>{node.full_name}</strong>
        {node.position ? <span className="muted"> · {node.position}</span> : null}
        {node.department ? <span className="muted"> · {node.department}</span> : null}
      </div>
      {node.reports?.length ? (
        <ul className="org-children">
          {node.reports.map((r) => <OrgNode key={r.id} node={r} depth={depth + 1} />)}
        </ul>
      ) : null}
    </li>
  );
}

export function OrgChartCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const employees = useEmployees(token);
  const [roots, setRoots] = useState([]);
  const [assign, setAssign] = useState({ employee_id: '', manager_id: '' });
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/org-chart', authHeaders(token)).then((res) => setRoots(res.data || [])).catch(capture);
  }, [token, capture]);
  useEffect(() => { load(); }, [load]);

  const setManager = (event) => {
    event.preventDefault();
    if (!assign.employee_id) return;
    api.patch(`/api/employees/${assign.employee_id}/manager`, {
      manager_id: assign.manager_id ? Number(assign.manager_id) : null,
    }, authHeaders(token)).then(() => { setAssign({ employee_id: '', manager_id: '' }); load(); }).catch(capture);
  };

  return (
    <section className="card">
      <h2>{t('orgChart')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      <form className="split split-three" onSubmit={setManager}>
        <label>{t('employee')}
          <select value={assign.employee_id} onChange={(e) => setAssign((c) => ({ ...c, employee_id: e.target.value }))}>
            <option value="">{t('selectEmployee')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <label>{t('reportsTo')}
          <select value={assign.manager_id} onChange={(e) => setAssign((c) => ({ ...c, manager_id: e.target.value }))}>
            <option value="">{t('noManager')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <div className="form-actions"><button type="submit" className="primary-button">{t('setManager')}</button></div>
      </form>
      {roots.length === 0 ? <p className="muted">{t('noOrgData')}</p> : (
        <ul className="org-tree">
          {roots.map((r) => <OrgNode key={r.id} node={r} depth={0} />)}
        </ul>
      )}
    </section>
  );
}

// Shifts -------------------------------------------------------------------

export function ShiftsCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const employees = useEmployees(token);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [tplForm, setTplForm] = useState({ name: '', start_time: '09:00', end_time: '17:00', is_night_shift: false });
  const [assignForm, setAssignForm] = useState({ employee_id: '', shift_template_id: '', work_date: '' });
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/shifts/templates', authHeaders(token)).then((res) => setTemplates(res.data || [])).catch(capture);
    api.get('/api/shifts/assignments', authHeaders(token)).then((res) => setAssignments(res.data || [])).catch(capture);
  }, [token, capture]);
  useEffect(() => { load(); }, [load]);

  const createTpl = (event) => {
    event.preventDefault();
    api.post('/api/shifts/templates', tplForm, authHeaders(token))
      .then(() => { setTplForm({ name: '', start_time: '09:00', end_time: '17:00', is_night_shift: false }); load(); })
      .catch(capture);
  };
  const createAssign = (event) => {
    event.preventDefault();
    api.post('/api/shifts/assignments', {
      employee_id: Number(assignForm.employee_id),
      shift_template_id: Number(assignForm.shift_template_id),
      work_date: assignForm.work_date,
    }, authHeaders(token)).then(() => { setAssignForm({ employee_id: '', shift_template_id: '', work_date: '' }); load(); }).catch(capture);
  };
  const employeeName = (id) => employees.find((e) => e.id === id)?.full_name || id;
  const templateName = (id) => templates.find((tpl) => tpl.id === id)?.name || id;

  return (
    <section className="card">
      <h2>{t('shiftScheduling')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      <form className="split split-three" onSubmit={createTpl}>
        <label>{t('shiftName')}<input value={tplForm.name} required onChange={(e) => setTplForm((c) => ({ ...c, name: e.target.value }))} /></label>
        <label>{t('startTime')}<input type="time" value={tplForm.start_time} onChange={(e) => setTplForm((c) => ({ ...c, start_time: e.target.value }))} /></label>
        <label>{t('endTime')}<input type="time" value={tplForm.end_time} onChange={(e) => setTplForm((c) => ({ ...c, end_time: e.target.value }))} /></label>
        <label className="checkbox-label"><input type="checkbox" checked={tplForm.is_night_shift} onChange={(e) => setTplForm((c) => ({ ...c, is_night_shift: e.target.checked }))} /> {t('nightShift')}</label>
        <div className="form-actions"><button type="submit" className="primary-button">{t('addShiftTemplate')}</button></div>
      </form>

      <form className="split split-three" onSubmit={createAssign}>
        <label>{t('employee')}
          <select value={assignForm.employee_id} required onChange={(e) => setAssignForm((c) => ({ ...c, employee_id: e.target.value }))}>
            <option value="">{t('selectEmployee')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <label>{t('shift')}
          <select value={assignForm.shift_template_id} required onChange={(e) => setAssignForm((c) => ({ ...c, shift_template_id: e.target.value }))}>
            <option value="">{t('selectShift')}</option>
            {templates.map((tpl) => <option key={tpl.id} value={tpl.id}>{tpl.name}</option>)}
          </select>
        </label>
        <label>{t('workDate')}<input type="date" value={assignForm.work_date} required onChange={(e) => setAssignForm((c) => ({ ...c, work_date: e.target.value }))} /></label>
        <div className="form-actions"><button type="submit" className="primary-button">{t('scheduleShift')}</button></div>
      </form>

      {assignments.length === 0 ? <p className="muted">{t('noShifts')}</p> : (
        <table className="data-table">
          <thead><tr><th>{t('workDate')}</th><th>{t('employee')}</th><th>{t('shift')}</th><th>Status</th></tr></thead>
          <tbody>
            {assignments.map((s) => (
              <tr key={s.id}><td>{s.work_date}</td><td>{employeeName(s.employee_id)}</td><td>{templateName(s.shift_template_id)}</td><td><StatusBadge status={s.status} /></td></tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Contracts ----------------------------------------------------------------

export function ContractsCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const employees = useEmployees(token);
  const [items, setItems] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [form, setForm] = useState({ employee_id: '', contract_type: 'permanent', start_date: '', end_date: '', monthly_salary: '' });
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/contracts', authHeaders(token)).then((res) => setItems(res.data || [])).catch(capture);
    api.get('/api/contracts/expiring', authHeaders(token, { within_days: 60 })).then((res) => setExpiring(res.data || [])).catch(() => {});
  }, [token, capture]);
  useEffect(() => { load(); }, [load]);

  const create = (event) => {
    event.preventDefault();
    api.post('/api/contracts', {
      employee_id: Number(form.employee_id),
      contract_type: form.contract_type,
      start_date: form.start_date,
      end_date: form.end_date || null,
      monthly_salary: Number(form.monthly_salary || 0),
    }, authHeaders(token)).then(() => { setForm({ employee_id: '', contract_type: 'permanent', start_date: '', end_date: '', monthly_salary: '' }); load(); }).catch(capture);
  };
  const sign = (id) => {
    const name = window.prompt(t('signerNamePrompt'));
    if (!name) return;
    api.post(`/api/contracts/${id}/sign`, { signed_by_name: name }, authHeaders(token)).then(load).catch(capture);
  };
  const employeeName = (id) => employees.find((e) => e.id === id)?.full_name || id;

  return (
    <section className="card">
      <h2>{t('contracts')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      {expiring.length ? (
        <p className="warning-text">{t('contractsExpiringSoon', { count: expiring.length })}</p>
      ) : null}
      <form className="split split-three" onSubmit={create}>
        <label>{t('employee')}
          <select value={form.employee_id} required onChange={(e) => setForm((c) => ({ ...c, employee_id: e.target.value }))}>
            <option value="">{t('selectEmployee')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <label>{t('contractType')}
          <select value={form.contract_type} onChange={(e) => setForm((c) => ({ ...c, contract_type: e.target.value }))}>
            <option value="permanent">Permanent</option>
            <option value="fixed_term">Fixed term</option>
            <option value="probation">Probation</option>
            <option value="contract">Contract</option>
          </select>
        </label>
        <label>{t('monthlySalary')} (ETB)<input type="number" min="0" step="0.01" value={form.monthly_salary} onChange={(e) => setForm((c) => ({ ...c, monthly_salary: e.target.value }))} /></label>
        <label>{t('startDate')}<input type="date" value={form.start_date} required onChange={(e) => setForm((c) => ({ ...c, start_date: e.target.value }))} /></label>
        <label>{t('endDate')}<input type="date" value={form.end_date} onChange={(e) => setForm((c) => ({ ...c, end_date: e.target.value }))} /></label>
        <div className="form-actions"><button type="submit" className="primary-button">{t('addContract')}</button></div>
      </form>

      {items.length === 0 ? <p className="muted">{t('noContracts')}</p> : (
        <table className="data-table">
          <thead><tr><th>{t('employee')}</th><th>{t('contractType')}</th><th>{t('startDate')}</th><th>{t('endDate')}</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id}>
                <td>{employeeName(c.employee_id)}</td>
                <td>{c.contract_type}</td>
                <td>{c.start_date}</td>
                <td>{c.end_date || '—'}</td>
                <td><StatusBadge status={c.status} /></td>
                <td className="row-actions">
                  {c.status !== 'active' || !c.signed_at ? (
                    <button type="button" className="ghost-button" onClick={() => sign(c.id)}>{t('sign')}</button>
                  ) : <span className="muted">{t('signed')}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Offboarding / severance --------------------------------------------------

export function OffboardingCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const activeLanguage = (useTranslation().i18n.resolvedLanguage || 'en').split('-')[0];
  const employees = useEmployees(token);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ employee_id: '', last_working_date: '', reason: 'resignation' });
  const [preview, setPreview] = useState(null);
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/offboarding', authHeaders(token)).then((res) => setItems(res.data || [])).catch(capture);
  }, [token, capture]);
  useEffect(() => { load(); }, [load]);

  const runPreview = () => {
    if (!form.employee_id || !form.last_working_date) return;
    api.post('/api/offboarding/preview', {
      employee_id: Number(form.employee_id), last_working_date: form.last_working_date, reason: form.reason,
    }, authHeaders(token)).then((res) => setPreview(res.data)).catch(capture);
  };
  const create = (event) => {
    event.preventDefault();
    api.post('/api/offboarding', {
      employee_id: Number(form.employee_id), last_working_date: form.last_working_date, reason: form.reason,
    }, authHeaders(token)).then(() => { setForm({ employee_id: '', last_working_date: '', reason: 'resignation' }); setPreview(null); load(); }).catch(capture);
  };
  const complete = (id) => api.post(`/api/offboarding/${id}/complete`, {}, authHeaders(token)).then(load).catch(capture);
  const employeeName = (id) => employees.find((e) => e.id === id)?.full_name || id;

  return (
    <section className="card">
      <h2>{t('offboarding')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      <form className="split split-three" onSubmit={create}>
        <label>{t('employee')}
          <select value={form.employee_id} required onChange={(e) => { setForm((c) => ({ ...c, employee_id: e.target.value })); setPreview(null); }}>
            <option value="">{t('selectEmployee')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <label>{t('reason')}
          <select value={form.reason} onChange={(e) => setForm((c) => ({ ...c, reason: e.target.value }))}>
            <option value="resignation">Resignation</option>
            <option value="termination">Termination</option>
            <option value="redundancy">Redundancy</option>
            <option value="retirement">Retirement</option>
            <option value="end_of_contract">End of contract</option>
          </select>
        </label>
        <label>{t('lastWorkingDate')}<input type="date" value={form.last_working_date} required onChange={(e) => { setForm((c) => ({ ...c, last_working_date: e.target.value })); setPreview(null); }} /></label>
        <div className="form-actions">
          <button type="button" className="ghost-button" onClick={runPreview}>{t('previewSettlement')}</button>
          <button type="submit" className="primary-button">{t('initiateOffboarding')}</button>
        </div>
      </form>

      {preview ? (
        <div className="settlement-preview">
          <h3>{t('settlementPreview')}</h3>
          <ul>
            <li>{t('yearsOfService')}: {preview.years_of_service}</li>
            <li>{t('severance')}: {formatCurrency(preview.severance_amount, activeLanguage)}</li>
            <li>{t('noticePay')}: {formatCurrency(preview.notice_pay_amount, activeLanguage)}</li>
            <li>{t('leavePayout')}: {formatCurrency(preview.unused_leave_payout, activeLanguage)}</li>
            <li><strong>{t('totalSettlement')}: {formatCurrency(preview.total_settlement, activeLanguage)}</strong></li>
          </ul>
        </div>
      ) : null}

      {items.length === 0 ? <p className="muted">{t('noOffboarding')}</p> : (
        <table className="data-table">
          <thead><tr><th>{t('employee')}</th><th>{t('reason')}</th><th>{t('lastWorkingDate')}</th><th>{t('totalSettlement')}</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map((o) => (
              <tr key={o.id}>
                <td>{employeeName(o.employee_id)}</td>
                <td>{o.reason}</td>
                <td>{o.last_working_date}</td>
                <td>{formatCurrency(o.total_settlement, activeLanguage)}</td>
                <td><StatusBadge status={o.status} /></td>
                <td className="row-actions">
                  {o.status !== 'completed' ? (
                    <button type="button" className="ghost-button" onClick={() => complete(o.id)}>{t('completeOffboarding')}</button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Disciplinary -------------------------------------------------------------

export function DisciplinaryCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const employees = useEmployees(token);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ employee_id: '', category: 'misconduct', severity: 'minor', summary: '' });
  const { error, capture } = useApiError();

  const load = useCallback(() => {
    if (!token) return;
    api.get('/api/disciplinary', authHeaders(token)).then((res) => setItems(res.data || [])).catch(capture);
  }, [token, capture]);
  useEffect(() => { load(); }, [load]);

  const create = (event) => {
    event.preventDefault();
    api.post('/api/disciplinary', {
      employee_id: Number(form.employee_id), category: form.category, severity: form.severity, summary: form.summary,
    }, authHeaders(token)).then(() => { setForm({ employee_id: '', category: 'misconduct', severity: 'minor', summary: '' }); load(); }).catch(capture);
  };
  const resolve = (id) => {
    const note = window.prompt(t('resolutionPrompt'));
    api.post(`/api/disciplinary/${id}/resolve`, { resolution_note: note || '', action_taken: note || '' }, authHeaders(token)).then(load).catch(capture);
  };
  const employeeName = (id) => employees.find((e) => e.id === id)?.full_name || id;

  return (
    <section className="card">
      <h2>{t('disciplinary')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      <form className="split split-three" onSubmit={create}>
        <label>{t('employee')}
          <select value={form.employee_id} required onChange={(e) => setForm((c) => ({ ...c, employee_id: e.target.value }))}>
            <option value="">{t('selectEmployee')}</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.full_name}</option>)}
          </select>
        </label>
        <label>{t('category')}
          <select value={form.category} onChange={(e) => setForm((c) => ({ ...c, category: e.target.value }))}>
            <option value="misconduct">Misconduct</option>
            <option value="attendance">Attendance</option>
            <option value="performance">Performance</option>
            <option value="grievance">Grievance</option>
          </select>
        </label>
        <label>{t('severity')}
          <select value={form.severity} onChange={(e) => setForm((c) => ({ ...c, severity: e.target.value }))}>
            <option value="minor">Minor</option>
            <option value="major">Major</option>
            <option value="gross">Gross</option>
          </select>
        </label>
        <label className="full-width">{t('summary')}<textarea value={form.summary} required onChange={(e) => setForm((c) => ({ ...c, summary: e.target.value }))} /></label>
        <div className="form-actions"><button type="submit" className="primary-button">{t('openCase')}</button></div>
      </form>

      {items.length === 0 ? <p className="muted">{t('noDisciplinary')}</p> : (
        <table className="data-table">
          <thead><tr><th>{t('employee')}</th><th>{t('category')}</th><th>{t('severity')}</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {items.map((d) => (
              <tr key={d.id}>
                <td>{employeeName(d.employee_id)}</td>
                <td>{d.category}</td>
                <td>{d.severity}</td>
                <td><StatusBadge status={d.status} /></td>
                <td className="row-actions">
                  {d.status !== 'resolved' ? (
                    <button type="button" className="ghost-button" onClick={() => resolve(d.id)}>{t('resolve')}</button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

// Workforce analytics ------------------------------------------------------

export function WorkforceAnalyticsCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const activeLanguage = (useTranslation().i18n.resolvedLanguage || 'en').split('-')[0];
  const [data, setData] = useState(null);
  const { error, capture } = useApiError();

  useEffect(() => {
    if (!token) return;
    api.get('/api/analytics/workforce', authHeaders(token)).then((res) => setData(res.data)).catch(capture);
  }, [token, capture]);

  const deptRows = useMemo(() => Object.entries(data?.headcount_by_department || {}), [data]);

  if (error) return <section className="card"><h2>{t('workforceAnalytics')}</h2><p className="error-text">{error}</p></section>;
  if (!data) return <section className="card"><h2>{t('workforceAnalytics')}</h2><p className="muted">{t('loading')}</p></section>;

  return (
    <section className="card">
      <h2>{t('workforceAnalytics')}</h2>
      <div className="metric-grid">
        <div className="metric"><span className="metric-value">{data.headcount}</span><span className="metric-label">{t('headcount')}</span></div>
        <div className="metric"><span className="metric-value">{data.attrition_rate_pct}%</span><span className="metric-label">{t('attritionRate')}</span></div>
        <div className="metric"><span className="metric-value">{data.avg_tenure_years}</span><span className="metric-label">{t('avgTenure')}</span></div>
        <div className="metric"><span className="metric-value">{formatCurrency(data.monthly_payroll_cost, activeLanguage)}</span><span className="metric-label">{t('monthlyPayrollCost')}</span></div>
        <div className="metric"><span className="metric-value">{formatCurrency(data.annual_payroll_cost, activeLanguage)}</span><span className="metric-label">{t('annualPayrollCost')}</span></div>
        <div className="metric"><span className="metric-value">{data.terminations_ytd}</span><span className="metric-label">{t('terminationsYtd')}</span></div>
      </div>
      {deptRows.length ? (
        <>
          <h3>{t('headcountByDepartment')}</h3>
          <ul className="bar-list">
            {deptRows.map(([dept, count]) => (
              <li key={dept}><span>{dept}</span><span className="bar"><span className="bar-fill" style={{ width: `${Math.min(100, (count / data.headcount) * 100)}%` }} /></span><span>{count}</span></li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

// Compliance (MoR export + Ethiopian calendar) -----------------------------

export function ComplianceCard() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [calendar, setCalendar] = useState(null);
  const { error, capture } = useApiError();

  useEffect(() => {
    if (!token) return;
    api.get('/api/calendar/ethiopian', authHeaders(token)).then((res) => setCalendar(res.data)).catch(capture);
  }, [token, capture]);

  const downloadMor = () => {
    api.get('/api/compliance/mor/paye-declaration.csv', { ...authHeaders(token), responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'mor-paye-declaration.csv';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(capture);
  };

  return (
    <section className="card">
      <h2>{t('compliance')}</h2>
      {error ? <p className="error-text">{error}</p> : null}
      {calendar ? (
        <p className="ethiopian-date">{t('todayEthiopian')}: <strong>{calendar.formatted_en}</strong> · {calendar.formatted_am}</p>
      ) : null}
      <p className="muted">{t('morExportHint')}</p>
      <button type="button" className="primary-button" onClick={downloadMor}>{t('exportMorDeclaration')}</button>
    </section>
  );
}
